import { useEffect, useId, useMemo, useState } from 'react'
import moment from 'moment'

import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Card from 'react-bootstrap/Card'
import ProgressBar from 'react-bootstrap/ProgressBar'
import Button from 'react-bootstrap/Button'
import ButtonGroup from 'react-bootstrap/ButtonGroup'

import { getOrganizationDashboardDaily } from '../../utilities/api'
import { useReportRefresh } from '../../hooks/useReportRefresh'
import { formatCurrency, formatNumber } from '../../utilities/helpers'
import { EmptyContainer } from '../EmptyContainer'
import { LoadingContainer } from '../LoadingContainer'
import { ReportRefreshButton } from '../ReportRefreshButton'

import './organizationDashboardWrapper.scss'

const DEFAULT_FROM = moment().subtract(6, 'days').startOf('day').toDate()
const DEFAULT_TO = moment().endOf('day').toDate()
const RANGE_PRESETS = ['7D', '30D', '90D', '1Y']

const PRESET_TITLE_LABELS = {
  '7D': 'Last 7 days',
  '30D': 'Last 30 days',
  '90D': 'Last 90 days',
  '1Y': 'Last year',
}

const formatPct = (value) => `${(Number(value || 0) * 100).toFixed(1)}%`
const getGrossRevenueWithAssignments = (summary) =>
  Number(summary?.grossRevenue || 0) + Number(summary?.assignmentRevenue || 0)

const csvEscape = (value) => {
  const stringValue = String(value ?? '')
  if (/[",\n]/.test(stringValue)) return `"${stringValue.replace(/"/g, '""')}"`
  return stringValue
}

function getDateRangeForPreset(preset) {
  const end = moment().endOf('day')

  switch (preset) {
    case '7D':
      return [
        moment(end).subtract(6, 'days').startOf('day').toDate(),
        end.toDate(),
      ]
    case '90D':
      return [
        moment(end).subtract(89, 'days').startOf('day').toDate(),
        end.toDate(),
      ]
    case '1Y':
      return [
        moment(end).subtract(1, 'year').startOf('day').toDate(),
        end.toDate(),
      ]
    case '30D':
    default:
      return [
        moment(end).subtract(29, 'days').startOf('day').toDate(),
        end.toDate(),
      ]
  }
}

function getDelta(currentValue, previousValue) {
  const current = Number(currentValue || 0)
  const previous = Number(previousValue || 0)
  if (previous === 0) return null
  return ((current - previous) / Math.abs(previous)) * 100
}

function buildSparklineCoordinates(
  values,
  width = 180,
  height = 46,
  padding = 4
) {
  if (!values?.length) return []
  const min = Math.min(...values)
  const max = Math.max(...values)
  const range = max - min || 1
  return values.map((value, index) => {
    const x =
      padding + (index * (width - padding * 2)) / Math.max(values.length - 1, 1)
    const y =
      height - padding - ((value - min) / range) * (height - padding * 2)
    return { x, y }
  })
}

function buildSparklinePoints(coords = []) {
  return coords.map((point) => `${point.x},${point.y}`).join(' ')
}

function buildSparklineAreaPath(coords = [], height = 46, padding = 4) {
  if (!coords.length) return ''
  const baseline = height - padding
  const first = coords[0]
  const last = coords[coords.length - 1]
  const linePath = coords.map((point) => `L ${point.x},${point.y}`).join(' ')
  return `M ${first.x},${baseline} ${linePath} L ${last.x},${baseline} Z`
}

function getNiceAxisMax(maxValue) {
  const numericMax = Number(maxValue || 0)
  if (!numericMax || numericMax < 0) return 1

  const magnitude = 10 ** Math.floor(Math.log10(numericMax))
  const normalized = numericMax / magnitude
  const factor =
    normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10
  return factor * magnitude
}

function buildTrendCoordinates(
  values,
  width = 100,
  height = 100,
  paddingX = 3,
  paddingY = 8
) {
  if (!values?.length) return []
  const max = Math.max(...values, 1)

  return values.map((value, index) => {
    const x =
      paddingX +
      (index * (width - paddingX * 2)) / Math.max(values.length - 1, 1)
    const y =
      height - paddingY - (Number(value || 0) / max) * (height - paddingY * 2)
    return { x, y }
  })
}

function buildSmoothLinePath(coords = []) {
  if (!coords.length) return ''
  if (coords.length === 1) return `M ${coords[0].x},${coords[0].y}`

  let path = `M ${coords[0].x},${coords[0].y}`
  for (let i = 1; i < coords.length; i += 1) {
    const prev = coords[i - 1]
    const current = coords[i]
    const controlX = (prev.x + current.x) / 2
    path += ` C ${controlX},${prev.y} ${controlX},${current.y} ${current.x},${current.y}`
  }

  return path
}

function buildTrendAreaPath(coords = [], height = 100, paddingY = 8) {
  if (!coords.length) return ''
  const baseline = height - paddingY
  const linePath = buildSmoothLinePath(coords)
  const last = coords[coords.length - 1]
  const first = coords[0]
  return `${linePath} L ${last.x},${baseline} L ${first.x},${baseline} Z`
}

function formatCompactCurrency(value) {
  const amount = Number(value || 0)
  if (!amount) return '$0'
  return `$${amount.toLocaleString(undefined, {
    notation: 'compact',
    maximumFractionDigits: 1,
  })}`
}

function formatCompactCount(value) {
  const amount = Number(value || 0)
  if (!amount) return '0'
  return amount.toLocaleString(undefined, {
    notation: 'compact',
    maximumFractionDigits: 1,
  })
}

function KpiCard({
  title,
  value,
  subtitle,
  delta,
  sparklineData = [],
  variant = 'teal',
}) {
  const sparklineId = useId().replace(/:/g, '')
  const sparklineCoords = buildSparklineCoordinates(sparklineData)
  const sparklinePoints = buildSparklinePoints(sparklineCoords)
  const sparklineAreaPath = buildSparklineAreaPath(sparklineCoords)
  const isPositive = delta == null ? null : delta >= 0
  const deltaLabel =
    delta == null
      ? '—'
      : `${isPositive ? '↑' : '↓'} ${Math.abs(delta).toFixed(1)}%`

  const gradientColorByVariant = {
    teal: '#29d8ff',
    purple: '#966cff',
    amber: '#ffc557',
    green: '#40e196',
  }
  const gradientColor = gradientColorByVariant[variant] || '#29d8ff'

  return (
    <Card
      body
      className={`card--xs card-with-border organization-dashboard-kpi organization-dashboard-kpi--${variant}`}
    >
      <div className="organization-dashboard-kpi-head">
        <Card.Title as="h6" className="text-muted mb-2">
          {title}
        </Card.Title>
        <span
          className={`organization-dashboard-kpi-delta ${
            isPositive == null
              ? 'is-neutral'
              : isPositive
                ? 'is-positive'
                : 'is-negative'
          }`}
        >
          {deltaLabel}
        </span>
      </div>
      <h3 className="mb-1">{value}</h3>
      {subtitle && <p className="mb-1 small text-muted">{subtitle}</p>}
      {sparklineData.length > 1 && (
        <div className="organization-dashboard-kpi-sparkline">
          <svg
            viewBox="0 0 180 46"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <defs>
              <linearGradient
                id={`sparkline-gradient-${sparklineId}`}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop
                  offset="0%"
                  stopColor={gradientColor}
                  stopOpacity="0.42"
                />
                <stop offset="100%" stopColor={gradientColor} stopOpacity="0" />
              </linearGradient>
            </defs>
            <path
              d={sparklineAreaPath}
              fill={`url(#sparkline-gradient-${sparklineId})`}
            />
            <polyline points={sparklinePoints} />
          </svg>
        </div>
      )}
    </Card>
  )
}

function RevenueTrend({ series, activeRangePreset }) {
  if (!series?.length) {
    return (
      <Card body className="organization-dashboard-revenue-trend-card">
        <EmptyContainer style="center lg report">
          <p>No daily revenue data in this date range.</p>
        </EmptyContainer>
      </Card>
    )
  }

  const maxRevenue = Math.max(
    ...series.map((d) => Number(d.gross_revenue || 0)),
    1
  )
  const rangeLabel = PRESET_TITLE_LABELS[activeRangePreset] || 'Selected range'

  return (
    <Card body className="organization-dashboard-revenue-trend-card">
      <Card.Title as="h5" className="mb-3">
        Daily Gross Revenue (Website + Box Office) ({rangeLabel})
      </Card.Title>
      <div className="organization-dashboard-trend-list">
        {series.map((row) => {
          const gross = Number(row.gross_revenue || 0)
          const pct = Math.max(0, Math.min(100, (gross / maxRevenue) * 100))
          return (
            <div
              key={row.activity_date}
              className="organization-dashboard-trend-row"
            >
              <span className="organization-dashboard-trend-date">
                {moment(row.activity_date).format('MMM D')}
              </span>
              <ProgressBar
                now={pct}
                className="organization-dashboard-trend-bar"
              />
              <span className="organization-dashboard-trend-value">
                {formatCurrency(gross)}
              </span>
            </div>
          )
        })}
      </div>
    </Card>
  )
}

const revenueSourceSummaryRows = (summary) => [
  {
    key: 'website',
    label: 'Website',
    value: summary?.websiteRevenue,
  },
  {
    key: 'box-office',
    label: 'Box Office',
    value: summary?.boxOfficeRevenue,
  },
  {
    key: 'ticket-assignment',
    label: 'Ticket Assignment',
    value: summary?.assignmentRevenue,
  },
]

function TopEventsCard({ topEvents }) {
  return (
    <Card
      body
      className="organization-dashboard-panel organization-dashboard-top-events-card"
    >
      <Card.Title as="h5" className="mb-1">
        Top Events
      </Card.Title>
      <p className="small text-muted mb-3">By gross revenue</p>
      {!topEvents?.length ? (
        <p className="text-muted mb-0">No event sales found in this range.</p>
      ) : (
        <div className="organization-dashboard-top-events">
          {topEvents.map((event, index) => (
            <div
              key={event.eventUuid || `${event.eventName}-${index}`}
              className="organization-dashboard-top-event-row"
            >
              <div className="organization-dashboard-top-event-meta">
                <span className="organization-dashboard-top-event-rank">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <span
                  className="organization-dashboard-top-event-accent"
                  aria-hidden="true"
                />
                <div className="organization-dashboard-top-event-details">
                  <strong className="organization-dashboard-top-event-name">
                    {event.eventName}
                  </strong>
                  <div className="organization-dashboard-top-event-subline">
                    {event.eventStart
                      ? moment(event.eventStart).format('MMM D')
                      : 'No date'}{' '}
                    · {formatNumber(event.ticketsSold)} sold ·{' '}
                    {formatNumber(event.ordersCount)} orders
                  </div>
                </div>
              </div>
              <strong className="organization-dashboard-top-event-revenue">
                {formatCurrency(event.grossRevenue)}
              </strong>
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}

function PurchaseHeatmapCard({ purchaseHeatmap }) {
  const days = purchaseHeatmap?.days ?? []
  const buckets = purchaseHeatmap?.buckets ?? []
  const maxTickets = Number(purchaseHeatmap?.maxTickets || 0)

  const getCellColor = (value) => {
    if (!maxTickets || value <= 0) return 'rgba(113, 57, 190, 0.18)'
    const intensity = Math.min(1, value / maxTickets)
    const alpha = 0.25 + intensity * 0.75
    return `rgba(0, 216, 255, ${alpha.toFixed(3)})`
  }

  return (
    <Card body className="organization-dashboard-panel">
      <Card.Title as="h5" className="mb-1">
        Purchase Heatmap
      </Card.Title>
      <p className="small text-muted mb-3">Tickets sold by day and time</p>
      {!days.length || !buckets.length ? (
        <p className="text-muted mb-0">No purchase activity for this range.</p>
      ) : (
        <div className="organization-dashboard-heatmap-wrap">
          <table className="organization-dashboard-heatmap-table">
            <thead>
              <tr>
                <th>Time</th>
                {days.map((day) => (
                  <th key={day}>{day}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {buckets.map((bucket) => (
                <tr key={bucket.label}>
                  <td>{bucket.label}</td>
                  {bucket.values.map((value, idx) => (
                    <td
                      key={`${bucket.label}-${days[idx]}`}
                      className="organization-dashboard-heatmap-cell"
                    >
                      <span
                        className="organization-dashboard-heatmap-cell-fill"
                        style={{
                          backgroundColor: getCellColor(Number(value || 0)),
                        }}
                        title={`${days[idx]} ${bucket.label}: ${formatNumber(value)} sold`}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}

const confidenceClassByScore = (score) => {
  if (score >= 0.8) return 'is-high'
  if (score >= 0.6) return 'is-medium'
  return 'is-low'
}

export function AiPricingRecommendationsCard({
  recommendations = [],
  summary = '',
  meta = null,
  isLoading = false,
}) {
  const hasRecommendations =
    Array.isArray(recommendations) && recommendations.length
  const metaStatus = meta?.status || ''
  const isUnavailable = metaStatus === 'failed' || metaStatus === 'disabled'
  const unavailableLabel =
    metaStatus === 'disabled'
      ? 'AI recommendations are currently disabled.'
      : 'AI recommendations are unavailable right now.'

  return (
    <Card
      body
      className="organization-dashboard-panel organization-dashboard-ai-pricing-card"
    >
      <Card.Title as="h5" className="mb-1">
        Pricing Recommendations
      </Card.Title>
      <p className="small text-muted mb-3">
        Recommendation only. No pricing changes are applied automatically.
      </p>
      {summary ? (
        <p className="organization-dashboard-ai-pricing-summary">{summary}</p>
      ) : null}
      {isLoading && !summary && !hasRecommendations ? (
        <p className="text-muted mb-0">Loading AI recommendations...</p>
      ) : null}
      {!hasRecommendations && !isLoading ? (
        <p className="text-muted mb-0">
          {isUnavailable
            ? unavailableLabel
            : 'No recommendations for this range.'}
        </p>
      ) : (
        <div className="organization-dashboard-ai-pricing-list">
          {recommendations.map((rec, index) => {
            const currentPrice = Number(rec?.currentPrice || 0)
            const recommendedPrice = Number(rec?.recommendedPrice || 0)
            const confidence = Number(rec?.confidence || 0)
            const confidencePct = Math.round(confidence * 100)
            return (
              <div
                key={`${rec?.ticketType || 'ticket'}-${index}`}
                className="organization-dashboard-ai-pricing-row"
              >
                <div className="organization-dashboard-ai-pricing-row-head">
                  <strong>{rec?.ticketType || 'All Tickets'}</strong>
                  <span
                    className={`organization-dashboard-ai-confidence ${confidenceClassByScore(
                      confidence
                    )}`}
                  >
                    {confidencePct}% confidence
                  </span>
                </div>
                <div className="organization-dashboard-ai-pricing-row-values">
                  <span>
                    Current: <strong>{formatCurrency(currentPrice)}</strong>
                  </span>
                  <span>
                    Suggested:{' '}
                    <strong>{formatCurrency(recommendedPrice)}</strong>
                  </span>
                </div>
                {rec?.reasoning ? (
                  <p className="organization-dashboard-ai-pricing-reasoning">
                    {rec.reasoning}
                  </p>
                ) : null}
              </div>
            )
          })}
        </div>
      )}
    </Card>
  )
}

function RevenueTicketSalesTrendCard({ series }) {
  const chartData = useMemo(
    () =>
      (series ?? []).map((row) => ({
        date: row.activity_date,
        revenue: Number(row.gross_revenue || 0),
        tickets: Number(
          row.tickets_sold_revenue_sources ?? row.tickets_sold ?? 0
        ),
      })),
    [series]
  )

  if (!chartData.length) {
    return (
      <Card body className="organization-dashboard-sales-trend-card mt-3">
        <Card.Title as="h5" className="mb-1">
          Revenue & Ticket Sales Trend
        </Card.Title>
        <p className="small text-muted mb-0">
          No trend data in this date range.
        </p>
      </Card>
    )
  }

  const revenueSeries = chartData.map((row) => row.revenue)
  const ticketSeries = chartData.map((row) => row.tickets)
  const revenueAxisMax = getNiceAxisMax(Math.max(...revenueSeries, 0))
  const ticketsAxisMax = getNiceAxisMax(Math.max(...ticketSeries, 0))

  const revenueCoords = buildTrendCoordinates(revenueSeries)
  const ticketCoords = buildTrendCoordinates(ticketSeries)
  const revenuePath = buildSmoothLinePath(revenueCoords)
  const ticketPath = buildSmoothLinePath(ticketCoords)
  const revenueArea = buildTrendAreaPath(revenueCoords)
  const ticketArea = buildTrendAreaPath(ticketCoords)

  const axisRatios = [1, 0.75, 0.5, 0.25, 0]
  const revenueTicks = axisRatios.map((ratio) => revenueAxisMax * ratio)
  const ticketTicks = axisRatios.map((ratio) => ticketsAxisMax * ratio)

  const xAxisStep = Math.max(1, Math.ceil(chartData.length / 6))
  const xAxisLabels = chartData
    .map((row, idx) => ({ ...row, idx }))
    .filter(
      ({ idx }) =>
        idx === 0 || idx === chartData.length - 1 || idx % xAxisStep === 0
    )

  return (
    <Card body className="organization-dashboard-sales-trend-card mt-3">
      <div className="organization-dashboard-sales-trend-header">
        <div>
          <Card.Title as="h5" className="mb-1">
            Revenue & Ticket Sales Trend
          </Card.Title>
          <p className="small text-muted mb-0">
            Daily performance over selected period (Website + Box Office)
          </p>
        </div>
        <div className="organization-dashboard-sales-trend-legend">
          <span>
            <i className="dot dot-revenue" aria-hidden="true" />
            Revenue (Website + Box Office)
          </span>
          <span>
            <i className="dot dot-tickets" aria-hidden="true" />
            Tickets (Website + Box Office)
          </span>
        </div>
      </div>

      <div className="organization-dashboard-sales-trend-canvas">
        <div className="organization-dashboard-sales-trend-y-axis left">
          {revenueTicks.map((tick) => (
            <span key={`revenue-tick-${tick}`}>
              {formatCompactCurrency(tick)}
            </span>
          ))}
        </div>
        <div className="organization-dashboard-sales-trend-chart-wrap">
          <svg
            className="organization-dashboard-sales-trend-chart"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <defs>
              <linearGradient
                id="organization-dashboard-revenue-area"
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="0%" stopColor="#00d8ff" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#00d8ff" stopOpacity="0" />
              </linearGradient>
              <linearGradient
                id="organization-dashboard-ticket-area"
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="0%" stopColor="#2eea9f" stopOpacity="0.28" />
                <stop offset="100%" stopColor="#2eea9f" stopOpacity="0" />
              </linearGradient>
            </defs>

            {axisRatios.map((ratio) => {
              const y = 8 + (1 - ratio) * 84
              return (
                <line
                  key={`grid-${ratio}`}
                  x1="3"
                  y1={y}
                  x2="97"
                  y2={y}
                  className="organization-dashboard-sales-trend-grid-line"
                />
              )
            })}

            <path
              d={revenueArea}
              className="organization-dashboard-sales-trend-area revenue"
            />
            <path
              d={ticketArea}
              className="organization-dashboard-sales-trend-area tickets"
            />

            <path
              d={revenuePath}
              className="organization-dashboard-sales-trend-line revenue"
            />
            <path
              d={ticketPath}
              className="organization-dashboard-sales-trend-line tickets"
            />
          </svg>

          <div className="organization-dashboard-sales-trend-x-axis">
            {xAxisLabels.map((row) => (
              <span
                key={`x-label-${row.date}-${row.idx}`}
                style={{
                  left: `${3 + row.idx * (94 / Math.max(chartData.length - 1, 1))}%`,
                }}
              >
                {moment(row.date).format('MMM D')}
              </span>
            ))}
          </div>
        </div>
        <div className="organization-dashboard-sales-trend-y-axis right">
          {ticketTicks.map((tick) => (
            <span key={`ticket-tick-${tick}`}>{formatCompactCount(tick)}</span>
          ))}
        </div>
      </div>
    </Card>
  )
}

function MonthlyAttendanceCard({ series }) {
  const monthlyData = useMemo(() => {
    if (!series?.length) return []

    const monthlyMap = new Map()
    series.forEach((row) => {
      const monthStart = moment(row.activity_date).startOf('month')
      const key = monthStart.format('YYYY-MM')
      const current = monthlyMap.get(key) || {
        key,
        label: monthStart.format('MMM'),
        attended: 0,
        noShows: 0,
      }

      const attended = Number(row.tickets_scanned || 0)
      const sold = Number(row.tickets_sold || 0)
      current.attended += attended
      current.noShows += Math.max(0, sold - attended)
      monthlyMap.set(key, current)
    })

    return [...monthlyMap.values()].sort((a, b) => a.key.localeCompare(b.key))
  }, [series])

  if (!monthlyData.length) {
    return (
      <Card
        body
        className="organization-dashboard-monthly-attendance-card mt-3"
      >
        <Card.Title as="h5" className="mb-1">
          Monthly Attendance
        </Card.Title>
        <p className="small text-muted mb-0">
          No attendance data in this date range.
        </p>
      </Card>
    )
  }

  const maxValue = Math.max(
    ...monthlyData.flatMap((month) => [month.attended, month.noShows]),
    1
  )
  const increment =
    maxValue > 3000 ? 1000 : maxValue > 1200 ? 500 : maxValue > 600 ? 250 : 100
  const axisMax = Math.max(
    increment,
    Math.ceil(maxValue / increment) * increment
  )
  const axisTicks = [4, 3, 2, 1, 0].map((step) => (axisMax * step) / 4)

  return (
    <Card body className="organization-dashboard-monthly-attendance-card mt-3">
      <div className="organization-dashboard-monthly-attendance-header">
        <div>
          <Card.Title as="h5" className="mb-1">
            Monthly Attendance
          </Card.Title>
          <p className="small text-muted mb-0">Scanned tickets at gate</p>
        </div>
        <div className="organization-dashboard-monthly-attendance-legend">
          <span>
            <i className="dot dot-attended" aria-hidden="true" />
            Attended
          </span>
          <span>
            <i className="dot dot-no-shows" aria-hidden="true" />
            No-Shows
          </span>
        </div>
      </div>

      <div className="organization-dashboard-monthly-attendance-chart">
        <div className="organization-dashboard-monthly-attendance-y-axis">
          {axisTicks.map((tick) => (
            <span key={tick}>{formatNumber(tick)}</span>
          ))}
        </div>
        <div className="organization-dashboard-monthly-attendance-plot">
          <div
            className="organization-dashboard-monthly-attendance-grid"
            aria-hidden="true"
          >
            {axisTicks.map((tick) => (
              <span key={`line-${tick}`} />
            ))}
          </div>
          <div className="organization-dashboard-monthly-attendance-columns">
            {monthlyData.map((month) => (
              <div
                key={month.key}
                className="organization-dashboard-monthly-attendance-column"
              >
                <div className="organization-dashboard-monthly-attendance-bars">
                  <span
                    className="bar bar-attended"
                    style={{ height: `${(month.attended / axisMax) * 100}%` }}
                    title={`${month.label} attended: ${formatNumber(month.attended)}`}
                  />
                  <span
                    className="bar bar-no-shows"
                    style={{ height: `${(month.noShows / axisMax) * 100}%` }}
                    title={`${month.label} no-shows: ${formatNumber(month.noShows)}`}
                  />
                </div>
                <span className="organization-dashboard-monthly-attendance-month">
                  {month.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Card>
  )
}

export default function OrganizationDashboardWrapper({
  organization,
  title = 'Dashboard',
  description = "A quick glance at your organization's daily revenue, orders, ticket sales, and scan performance.",
}) {
  const [activeRangePreset, setActiveRangePreset] = useState('7D')
  const [dateRange, setDateRange] = useState([DEFAULT_FROM, DEFAULT_TO])
  const [isLoading, setIsLoading] = useState(false)
  const { refreshToken, refresh, completeRefresh } = useReportRefresh(isLoading)
  const [isAiPricingLoading, setIsAiPricingLoading] = useState(false)
  const [dashboardData, setDashboardData] = useState(null)
  const [aiPricingData, setAiPricingData] = useState(null)
  const [previousSummary, setPreviousSummary] = useState(null)
  const [loadError, setLoadError] = useState('')

  const [fromDate, toDate] = dateRange

  useEffect(() => {
    let isCancelled = false

    const load = async () => {
      if (!organization?.uuid || !fromDate || !toDate) return
      setIsLoading(true)
      setIsAiPricingLoading(false)
      setLoadError('')
      setAiPricingData(null)
      try {
        const from = moment(fromDate).format('YYYY-MM-DD')
        const to = moment(toDate).format('YYYY-MM-DD')

        const dayCount =
          moment(to, 'YYYY-MM-DD')
            .startOf('day')
            .diff(moment(from, 'YYYY-MM-DD').startOf('day'), 'days') + 1
        const prevTo = moment(from, 'YYYY-MM-DD').subtract(1, 'day')
        const prevFrom = moment(prevTo).subtract(dayCount - 1, 'days')

        const [currentRes, previousRes] = await Promise.all([
          getOrganizationDashboardDaily(organization.uuid, from, to, {
            includeAiPricing: false,
          }),
          getOrganizationDashboardDaily(
            organization.uuid,
            prevFrom.format('YYYY-MM-DD'),
            prevTo.format('YYYY-MM-DD'),
            { includeAiPricing: false }
          ),
        ])

        if (isCancelled) return
        setDashboardData(currentRes?.data ?? null)
        setPreviousSummary(previousRes?.data?.summary ?? null)
      } catch (error) {
        if (isCancelled) return
        console.error('Failed to load organization dashboard data', error)
        setDashboardData(null)
        setPreviousSummary(null)
        setLoadError(
          'Unable to load dashboard data for the selected date range.'
        )
      } finally {
        if (!isCancelled) {
          setIsLoading(false)
          completeRefresh()
        }
      }
    }

    load()

    return () => {
      isCancelled = true
    }
  }, [organization?.uuid, fromDate, toDate, refreshToken])

  useEffect(() => {
    let isCancelled = false

    const loadAiRecommendations = async () => {
      if (!organization?.uuid || !fromDate || !toDate || !dashboardData) return

      const from = moment(fromDate).format('YYYY-MM-DD')
      const to = moment(toDate).format('YYYY-MM-DD')

      setIsAiPricingLoading(true)
      try {
        const aiRes = await getOrganizationDashboardDaily(
          organization.uuid,
          from,
          to,
          {
            includeAiPricing: true,
          }
        )
        if (isCancelled) return
        const aiData = aiRes?.data ?? {}
        setAiPricingData({
          recommendations: aiData?.pricingRecommendations ?? [],
          summary: aiData?.pricingRecommendationSummary ?? '',
          meta: aiData?.pricingRecommendationsMeta ?? null,
        })
      } catch (error) {
        if (isCancelled) return
        console.error('Failed to load AI pricing recommendations', error)
        setAiPricingData({
          recommendations: [],
          summary: '',
          meta: {
            status: 'failed',
            reason: 'ai_pricing_fetch_error',
          },
        })
      } finally {
        if (!isCancelled) {
          setIsAiPricingLoading(false)
        }
      }
    }

    loadAiRecommendations()

    return () => {
      isCancelled = true
    }
  }, [organization?.uuid, fromDate, toDate, dashboardData])

  const summary = dashboardData?.summary
  const series = dashboardData?.series ?? []
  const topEvents = dashboardData?.topEvents ?? []
  const purchaseHeatmap = dashboardData?.purchaseHeatmap ?? null
  const pricingRecommendations = aiPricingData?.recommendations ?? []
  const pricingRecommendationSummary = aiPricingData?.summary ?? ''
  const pricingRecommendationsMeta = aiPricingData?.meta ?? null
  const isAiPricingPending =
    Boolean(dashboardData) && !aiPricingData && !loadError && !isLoading
  const grossRevenueWithAssignments = getGrossRevenueWithAssignments(summary)
  const previousGrossRevenueWithAssignments =
    getGrossRevenueWithAssignments(previousSummary)

  const totals = useMemo(
    () => ({
      grossRevenue: formatCurrency(grossRevenueWithAssignments),
      netRevenue: formatCurrency(summary?.netRevenue),
      ordersCount: formatNumber(summary?.ordersCount),
      ticketsSold: formatNumber(summary?.ticketsSold),
      ticketsScanned: formatNumber(summary?.ticketsScanned),
      avgPaidOrderValue: formatCurrency(summary?.avgPaidOrderValue),
      scanRate: formatPct(summary?.scanRate),
      uniqueBuyers: formatNumber(summary?.uniqueBuyers),
      eventsWithSales: formatNumber(summary?.eventsWithSales),
    }),
    [grossRevenueWithAssignments, summary]
  )

  const sparklineSeries = useMemo(
    () => ({
      gross: series.map(
        (row) =>
          Number(row.gross_revenue || 0) + Number(row.assignment_revenue || 0)
      ),
      net: series.map((row) => Number(row.net_revenue || 0)),
      orders: series.map((row) => Number(row.orders_count || 0)),
      tickets: series.map((row) => Number(row.tickets_sold || 0)),
    }),
    [series]
  )

  const handleExport = () => {
    if (!dashboardData?.series?.length) return

    const from = moment(fromDate).format('YYYY-MM-DD')
    const to = moment(toDate).format('YYYY-MM-DD')
    const filename = `organization-dashboard-${organization?.uuid || 'org'}-${from}-to-${to}.csv`

    const summaryRows = [
      ['Metric', 'Value'],
      ['Gross Revenue (Website + Box Office)', summary?.grossRevenue ?? 0],
      ['Net Revenue', summary?.netRevenue ?? 0],
      ['Orders', summary?.ordersCount ?? 0],
      ['Paid Orders', summary?.paidOrdersCount ?? 0],
      ['Complimentary Orders', summary?.complimentaryOrdersCount ?? 0],
      ['Tickets Sold', summary?.ticketsSold ?? 0],
      ['Tickets Scanned', summary?.ticketsScanned ?? 0],
      ['Scan Rate', summary?.scanRate ?? 0],
      ['Average Paid Order Value', summary?.avgPaidOrderValue ?? 0],
      ['Unique Buyers', summary?.uniqueBuyers ?? 0],
      ['Events With Sales', summary?.eventsWithSales ?? 0],
      ['Website Revenue', summary?.websiteRevenue ?? 0],
      ['Box Office Revenue', summary?.boxOfficeRevenue ?? 0],
      ['Assignment Revenue', summary?.assignmentRevenue ?? 0],
    ]

    const dailyHeader = [
      'activity_date',
      'gross_revenue',
      'net_revenue',
      'orders_count',
      'paid_orders_count',
      'complimentary_orders_count',
      'tickets_sold',
      'tickets_sold_revenue_sources',
      'tickets_scanned',
      'scan_rate',
      'website_revenue',
      'box_office_revenue',
      'assignment_revenue',
      'unique_buyers',
      'events_with_sales',
      'avg_paid_order_value',
    ]

    const dailyRows = dashboardData.series.map((row) =>
      dailyHeader.map((column) => row?.[column] ?? '')
    )

    const csvLines = [
      ...summaryRows.map((row) => row.map(csvEscape).join(',')),
      '',
      'Daily Breakdown',
      dailyHeader.join(','),
      ...dailyRows.map((row) => row.map(csvEscape).join(',')),
    ]

    const blob = new Blob([csvLines.join('\n')], {
      type: 'text/csv;charset=utf-8;',
    })

    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = filename
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
    URL.revokeObjectURL(url)
  }

  return (
    <section className="spacer-sm" id="organization-dashboard-wrapper">
      <header className="section-header-sm">
        <div className="section-heading">
          <h1>{title}</h1>
        </div>
        <p className="section-header-desc">{description}</p>
      </header>

      <div className="organization-dashboard-actions mb-4">
        <div className="organization-dashboard-range-group">
          <ButtonGroup aria-label="Dashboard range presets">
            {RANGE_PRESETS.map((preset) => (
              <Button
                key={preset}
                className={`organization-dashboard-range-btn ${
                  activeRangePreset === preset ? 'is-active' : ''
                }`}
                disabled={isLoading}
                onClick={() => {
                  setActiveRangePreset(preset)
                  setDateRange(getDateRangeForPreset(preset))
                }}
              >
                {preset}
              </Button>
            ))}
          </ButtonGroup>
        </div>
        <ReportRefreshButton
          onRefresh={refresh}
          isRefreshing={isLoading}
          disabled={isLoading || !fromDate || !toDate}
        />
        <Button
          variant="primary"
          className="organization-dashboard-export-btn"
          disabled={isLoading || !series.length}
          onClick={handleExport}
        >
          Export
        </Button>
      </div>

      {isLoading ? (
        <LoadingContainer />
      ) : loadError ? (
        <Card body>
          <EmptyContainer style="center lg report">
            <p>{loadError}</p>
          </EmptyContainer>
        </Card>
      ) : !summary ? (
        <Card body>
          <EmptyContainer style="center lg report">
            <p>No dashboard data found.</p>
          </EmptyContainer>
        </Card>
      ) : (
        <>
          <Row className="g-3 mb-3">
            <Col md={6} lg={4} xl={3}>
              <KpiCard
                title="Gross Revenue"
                value={totals.grossRevenue}
                subtitle={`vs ${formatCurrency(previousGrossRevenueWithAssignments)} last period`}
                delta={getDelta(
                  grossRevenueWithAssignments,
                  previousGrossRevenueWithAssignments
                )}
                sparklineData={sparklineSeries.gross}
                variant="teal"
              />
            </Col>
            <Col md={6} lg={4} xl={3}>
              <KpiCard
                title="Net Revenue"
                value={totals.netRevenue}
                subtitle={`vs ${formatCurrency(previousSummary?.netRevenue)} last period`}
                delta={getDelta(
                  summary?.netRevenue,
                  previousSummary?.netRevenue
                )}
                sparklineData={sparklineSeries.net}
                variant="purple"
              />
            </Col>
            <Col md={6} lg={4} xl={3}>
              <KpiCard
                title="Orders"
                value={totals.ordersCount}
                subtitle={`vs ${formatNumber(previousSummary?.ordersCount)} last period`}
                delta={getDelta(
                  summary?.ordersCount,
                  previousSummary?.ordersCount
                )}
                sparklineData={sparklineSeries.orders}
                variant="amber"
              />
            </Col>
            <Col md={6} lg={4} xl={3}>
              <KpiCard
                title="Tickets Sold"
                value={totals.ticketsSold}
                subtitle={`vs ${formatNumber(previousSummary?.ticketsSold)} last period`}
                delta={getDelta(
                  summary?.ticketsSold,
                  previousSummary?.ticketsSold
                )}
                sparklineData={sparklineSeries.tickets}
                variant="green"
              />
            </Col>
            <Col md={6} lg={4} xl={3}>
              <KpiCard title="Tickets Scanned" value={totals.ticketsScanned} />
            </Col>
            <Col md={6} lg={4} xl={3}>
              <KpiCard title="Scan Rate" value={totals.scanRate} />
            </Col>
            <Col md={6} lg={4} xl={3}>
              <KpiCard
                title="Avg Paid Order"
                value={totals.avgPaidOrderValue}
              />
            </Col>
            <Col md={6} lg={4} xl={3}>
              <KpiCard
                title="Unique Buyers / Events"
                value={`${totals.uniqueBuyers} / ${totals.eventsWithSales}`}
              />
            </Col>
          </Row>

          <Row className="g-3 organization-dashboard-revenue-row">
            <Col xl={7}>
              <RevenueTrend
                series={series}
                activeRangePreset={activeRangePreset}
              />
            </Col>
            <Col xl={5}>
              <Card body className="organization-dashboard-revenue-source-card">
                <Card.Title as="h5" className="mb-3">
                  Revenue by Source (Range Total)
                </Card.Title>
                <div className="organization-dashboard-source-grid">
                  {revenueSourceSummaryRows(summary).map((source) => (
                    <div
                      key={source.key}
                      className="organization-dashboard-source-row"
                    >
                      <div className="organization-dashboard-source-meta">
                        <span
                          className="organization-dashboard-source-accent"
                          aria-hidden="true"
                        />
                        <span>{source.label}</span>
                      </div>
                      <strong>{formatCurrency(source.value)}</strong>
                    </div>
                  ))}
                </div>
              </Card>
            </Col>
          </Row>

          <Row className="g-3 mt-1">
            <Col xl={6}>
              <TopEventsCard topEvents={topEvents} />
            </Col>
            <Col xl={6}>
              <PurchaseHeatmapCard purchaseHeatmap={purchaseHeatmap} />
            </Col>
          </Row>

          <Row className="g-3 mt-1">
            <Col xl={12}>
              <AiPricingRecommendationsCard
                recommendations={pricingRecommendations}
                summary={pricingRecommendationSummary}
                meta={pricingRecommendationsMeta}
                isLoading={isAiPricingLoading || isAiPricingPending}
              />
            </Col>
          </Row>

          <RevenueTicketSalesTrendCard series={series} />
          <MonthlyAttendanceCard series={series} />
        </>
      )}
    </section>
  )
}
